import { recordToKeyValuePair } from '../helpers/array.helpers';
import { KeyValuePair, SystemId } from '../interfaces';
import {
  isCustomerCareColorScheme,
  type CustomerCareColorSchemeMode
} from './customerCareColorScheme';

export {
  CustomerCareColorScheme,
  isCustomerCareColorScheme
} from './customerCareColorScheme';
export type { CustomerCareColorSchemeMode } from './customerCareColorScheme';

export const getUserForceHeaderLanguageOverridePreferenceKey = (
  systemId: SystemId
) => {
  return `${systemId}-LANGUAGE-HEADER-FORCE-OVERRIDE`;
};

export const getUserLanguagePreferenceKey = (systemId: SystemId) => {
  return `${systemId}-LANGUAGE-HEADER`;
};

export const getUserDisplayLanguagePreferenceKey = (systemId: SystemId) => {
  return `${systemId}-LANGUAGE`;
};

export const getUserSalesChannelPreferenceKey = (systemId: SystemId) => {
  return `${systemId}-SALES-CHANNEL-ID`;
};

export const getUserInventoryStorePreferenceKey = (systemId: SystemId) => {
  return `${systemId}-INVENTORY-STORE-ID`;
};

export const getUserFeatureToggleOverridesPreferenceKey = (
  systemId: SystemId
) => {
  return `${systemId}-FEATURE-TOGGLE-OVERRIDES`;
};

/**
 * Key for the per-business-unit "resume last URL" preference. Stores the last
 * page a user was on within a business unit so they can be returned there after
 * re-authenticating (e.g. following an idle-timeout logout).
 *
 * Keyed by the active business unit's `SystemId` so a user's resume location is
 * scoped to the business unit they were working in, matching the other per-BU
 * preference keys (language, sales channel, etc.).
 */
export const getUserLastUrlPreferenceKey = (systemId: SystemId) => {
  return `${systemId}-LAST-URL`;
};

/**
 * Per-business-unit appearance choice for Customer Care. A missing key means
 * this user follows the business unit's color scheme.
 */
export const getUserColorSchemePreferenceKey = (systemId: SystemId) => {
  return `${systemId}-CUSTOMER-CARE-COLOR-SCHEME`;
};

export function readUserColorSchemePreference(
  preferences: Record<string, string>,
  systemId: SystemId
): CustomerCareColorSchemeMode | undefined {
  const value = preferences[getUserColorSchemePreferenceKey(systemId)];

  return isCustomerCareColorScheme(value) ? value : undefined;
}

/**
 * Store or clear this user's Customer Care appearance for the business unit.
 * The full preference set is returned so unrelated preferences stay in place.
 * Anything other than light, dark, or system clears the key.
 */
export const buildPreferencesWithColorScheme = (
  preferences: Record<string, string>,
  systemId: SystemId,
  scheme: string | undefined
): KeyValuePair<string>[] => {
  const key = getUserColorSchemePreferenceKey(systemId);
  const nextPreferences = { ...preferences };

  if (isCustomerCareColorScheme(scheme)) {
    nextPreferences[key] = scheme;
  } else {
    delete nextPreferences[key];
  }

  return recordToKeyValuePair(nextPreferences);
};

/**
 * Merge the "resume last URL" for a business unit into an existing preference
 * set and return the full key-value array to send to `updatePreferences`.
 *
 * The whole set is returned (not just the changed key) so unrelated preferences
 * are preserved when the server stores the update.
 */
export const buildPreferencesWithLastUrl = (
  preferences: Record<string, string>,
  systemId: SystemId,
  url: string
): KeyValuePair<string>[] => {
  return recordToKeyValuePair({
    ...preferences,
    [getUserLastUrlPreferenceKey(systemId)]: url
  });
};

/**
 * Remove the "resume last URL" for a business unit from an existing preference
 * set and return the full key-value array to send to `updatePreferences`.
 *
 * Used after the resume location has been consumed so a later no-op re-login
 * does not repeatedly resume to the same page. The whole set is returned so
 * unrelated preferences are preserved.
 */
export const buildPreferencesWithoutLastUrl = (
  preferences: Record<string, string>,
  systemId: SystemId
): KeyValuePair<string>[] => {
  const key = getUserLastUrlPreferenceKey(systemId);
  const { [key]: _removed, ...remainingPreferences } = preferences;

  return recordToKeyValuePair(remainingPreferences);
};
